import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./pages/Auth/Login";
import LuckyDraw from "./pages/LuckyDraw";
import UserDashboard from "./pages/UserDashboard";
import AgencyDashboard from "./pages/AgencyDashboard";
import EmployeeDashboard from "./pages/EmployeeDashboard";
import ProtectedRoute from "./components/ProtectedRoute";
import HomeScreen from "./pages/HomeScreen";
import ConsumerCoupon from "./pages/ConsumerCoupon";
import ConsumerKYC from "./pages/ConsumerKYC";
import AgencyLuckyCoupons from "./pages/AgencyLuckyCoupons";
import EmployeeLuckyCoupons from "./pages/EmployeeLuckyCoupons";
import ProductUpload from "./pages/agency/products/ProductUpload";
import ProductList from "./pages/agency/products/ProductList";
import PurchaseRequests from "./pages/agency/purchase/PurchaseRequests";
import ProductDetails from "./pages/market/ProductDetails";
import MyOrders from "./pages/market/MyOrders";
import BannerDetails from "./pages/market/BannerDetails";
import BannerManage from "./pages/agency/banners/BannerManage";
import ConsumerShell from "./components/layouts/ConsumerShell";
import AgencyShell from "./components/layouts/AgencyShell";
import EmployeeShell from "./components/layouts/EmployeeShell";
import EnhancedLogin from "./pages/Auth/EnhancedLogin";
import { isGrowthProduction } from "./utils/growthProductionGuard";
import Wallet from "./pages/Wallet";
import History from "./pages/History";
import AgencyHistory from "./pages/agency/AgencyHistory";
import LoadingOverlay from "./components/LoadingOverlay";
import AppLifecycleSync from "./components/common/AppLifecycleSync";
import MyTeam from "./pages/team/MyTeam";
import Genealogy5 from "./pages/team/Genealogy5";
import TeamDocumentDownload from "./pages/team/TeamDocumentDownload";
import TeamWalletHistory from "./pages/team/TeamWalletHistory";
import PrimeInvoices from "./pages/team/PrimeInvoices";
import EmployeeDailyReport from "./pages/reports/EmployeeDailyReport";
import AgencyDailyReport from "./pages/reports/AgencyDailyReport";
import AdminProtectedRoute from "./components/AdminProtectedRoute";
import AdminShell from "./components/layouts/AdminShell";
import AdminFranchiseShell from "./components/layouts/AdminFranchiseShell";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminFranchiseDashboard from "./pages/admin/AdminFranchiseDashboard";
import AdminFranchiseCategoryDashboard from "./pages/admin/AdminFranchiseCategoryDashboard";
import AdminFranchiseUsers from "./pages/admin/AdminFranchiseUsers";
import AdminFranchiseWalletControls from "./pages/admin/AdminFranchiseWalletControls";
import AdminFranchisePlaceholder from "./pages/admin/AdminFranchisePlaceholder";
import AdminUserTree from "./pages/admin/AdminUserTree";
import AdminAnalytics from "./pages/admin/AdminAnalytics";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminMerchants from "./pages/admin/AdminMerchants";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminKYC from "./pages/admin/AdminKYC";
import AdminWithdrawals from "./pages/admin/AdminWithdrawals";
import AdminFranchiseAchievers from "./pages/admin/AdminFranchiseAchievers";
import AdminFranchiseWishingBanners from "./pages/admin/AdminFranchiseWishingBanners";
import AdminFranchiseSupport from "./pages/admin/AdminFranchiseSupport";
import AdminTeamConsumerWishingBanners from "./pages/admin/AdminTeamConsumerWishingBanners";
import AdminTeamConsumerTopAchievers from "./pages/admin/AdminTeamConsumerTopAchievers";
import AdminTeamConsumerEducationalVideos from "./pages/admin/AdminTeamConsumerEducationalVideos";
import AdminTeamConsumerPdfUploads from "./pages/admin/AdminTeamConsumerPdfUploads";
import AdminTeamConsumerCertificateUploads from "./pages/admin/AdminTeamConsumerCertificateUploads";
import AdminTeamConsumerBlockUsers from "./pages/admin/AdminTeamConsumerBlockUsers";
import AdminMatrixFive from "./pages/admin/AdminMatrixFive";
import AdminMatrixThree from "./pages/admin/AdminMatrixThree";
import AdminAutopool from "./pages/admin/AdminAutopool";
import AdminMatrixReconcile from "./pages/admin/AdminMatrixReconcile";
import AdminECoupons from "./pages/admin/AdminECoupons";
import AdminPayments from "./pages/admin/AdminPayments";
import AdminAgencyPrimeRequests from "./pages/admin/AdminAgencyPrimeRequests";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminBanners from "./pages/admin/AdminBanners";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminUploads from "./pages/admin/AdminUploads";
import AdminPackages from "./pages/admin/AdminPackages";
import AdminBusiness from "./pages/admin/AdminBusiness";
import AdminReports from "./pages/admin/AdminReports";
import AdminPromoPurchases from "./pages/admin/AdminPromoPurchases";
import AdminPackagePromoPurchases from "./pages/admin/AdminPackagePromoPurchases";
import AdminJoinSubscriptionPurchases from "./pages/admin/AdminJoinSubscriptionPurchases";
import AdminSPPPurchases from "./pages/admin/AdminSPPPurchases";
import AdminSPPSeasons from "./pages/admin/AdminSPPSeasons";
import AdminTriTourPurchases from "./pages/admin/AdminTriTourPurchases";
import AdminDigitalEducationPrimeApprovals from "./pages/admin/AdminDigitalEducationPrimeApprovals";
import AdminJoinPrimeLedger from "./pages/admin/AdminJoinPrimeLedger";
import AdminSPPLedger from "./pages/admin/AdminSPPLedger";
import AdminTriTourLedger from "./pages/admin/AdminTriTourLedger";
import AdminDigitalEducationLedger from "./pages/admin/AdminDigitalEducationLedger";
import AdminWalletUploadApprovals from "./pages/admin/AdminWalletUploadApprovals";
import AdminWallets from "./pages/admin/AdminWallets";
import AdminWalletDetail from "./pages/admin/AdminWalletDetail";
import AdminWalletVouchers from "./pages/admin/AdminWalletVouchers";
import AdminWalletReconcile from "./pages/admin/AdminWalletReconcile";
import AdminUserAudit from "./pages/admin/AdminUserAudit";
import AdminWalletCommandCenter from "./pages/admin/AdminWalletCommandCenter";
import AdminWalletLedger from "./pages/admin/AdminWalletLedger";
import AdminTeamWalletDashboard from "./pages/admin/AdminTeamWalletDashboard";
import AdminWalletMonitoring from "./pages/admin/AdminWalletMonitoring";
import AdminPackageManagement from "./pages/admin/AdminPackageManagement";
import AdminRewardDistribution from "./pages/admin/AdminRewardDistribution";
import AdminWalletSettlements from "./pages/admin/AdminWalletSettlements";
import AdminPromoPackageProducts from "./pages/admin/AdminPromoPackageProducts";
import AdminDashboardCards from "./pages/admin/AdminDashboardCards";
import AdminHomeCards from "./pages/admin/AdminHomeCards";
import AdminLuckyDraw from "./pages/admin/AdminLuckyDraw";
import AdminLevelCommission from "./pages/admin/AdminLevelCommission";
import AdminMatrixCommission from "./pages/admin/AdminMatrixCommission";
import AdminCommissionHistory from "./pages/admin/AdminCommissionHistory";
import AdminCommissionDistribute from "./pages/admin/AdminCommissionDistribute";
import AdminSSVVouchers from "./pages/admin/AdminSSVVouchers";
import AdminAgentTrainingVouchers from "./pages/admin/AdminAgentTrainingVouchers";
import AdminCycleManagement from "./pages/admin/AdminCycleManagement";
import AdminValidatorCommission from "./pages/admin/AdminValidatorCommission";
import AdminWalletDebugger from "./pages/admin/AdminWalletDebugger";
import AdminDailySalesReport from "./pages/admin/AdminDailySalesReport";
import AdminLedgerStatement from "./pages/admin/AdminLedgerStatement";
import AdminInternalWalletDailyReport from "./pages/admin/AdminInternalWalletDailyReport";
import AdminUsersTodayReport from "./pages/admin/AdminUsersTodayReport";
import Profile from "./pages/Profile";
import RoleSelect from "./pages/Auth/RoleSelect";
import ReferAndEarnPage from "./pages/ReferAndEarn";
import AgencyReferAndEarn from "./pages/AgencyReferAndEarn";
import WealthGalaxy from "./pages/WealthGalaxy";
import AppHub from "./pages/AppHub";
import ModelsIndex from "./admin-panel/dynamic/ModelsIndex";
import ModelList from "./admin-panel/dynamic/ModelList";
import UsersPage from "./admin-panel/examples/UsersPage";
import ProductPage from "./admin-panel/examples/ProductPage";
import Support from "./pages/Support";
import AdminSupport from "./pages/admin/AdminSupport";
import AdminNotifications from "./pages/admin/AdminNotifications";
import ImpersonateLanding from "./pages/Auth/ImpersonateLanding";
import ECouponStore from "./pages/ECouponStore";
import CouponPocket from "./pages/CouponPocket";
import PackageCouponPocket from "./pages/PackageCouponPocket";
import PromoPackages from "./pages/PromoPackages";
import PackageSummary from "./pages/PackageSummary";
import JoinSubscription from "./pages/packages/JoinSubscription";
import SPP from "./pages/packages/SPP";
import SPPGiftCards from "./pages/packages/SPPGiftCards";
import DigitalEducationPrime from "./pages/packages/DigitalEducationPrime";
import EducationalVideoSummary from "./pages/packages/EducationalVideoSummary";
import PromoProducts from "./pages/PromoProducts";
import TrikonektProducts from "./pages/TrikonektProducts";
import AgencyMarketplace from "./pages/agency/AgencyMarketplace";
import AdminRewardPoints from "./pages/admin/AdminRewardPoints";
import Cart from "./pages/Cart";
import CheckoutV2 from "./pages/CheckoutV2";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import Payment from "./pages/Payment";
import RegisterV2 from "./pages/Auth/RegisterV2";
import MyOrdersAll from "./pages/MyOrdersAll";
import TriAppPage from "./pages/TriAppPage";
import AdminTriAppManage from "./pages/admin/AdminTriAppManage";
import AdminAccessManager from "./pages/admin/AdminAccessManager";
import AdminAdminUsers from "./pages/admin/AdminAdminUsers";
import RolesPage from "./pages/admin/RolesPage";
import PermissionsPage from "./pages/admin/PermissionsPage";
import UserPermissionMap from "./pages/admin/UserPermissionMap";
import AdminMerchantCategories from "./pages/admin/AdminMerchantCategories";
import AdminMerchantSubcategories from "./pages/admin/AdminMerchantSubcategories";
import AdminWorkflowScreen from "./pages/admin/AdminWorkflowScreen";
import BusinessShell from "./components/layouts/BusinessShell";
import BusinessDashboard from "./pages/business/BusinessDashboard";
import BusinessProfile from "./pages/business/BusinessProfile";
import BusinessShops from "./pages/business/BusinessShops";
import BusinessShopProducts from "./pages/business/BusinessShopProducts";
import MerchantShops from "./pages/market/MerchantShops";
import MerchantShopDetail from "./pages/market/MerchantShopDetail";
import AgencyCoupons from "./pages/agency/AgencyCoupons";
import AgencyPrimePackage from "./pages/agency/AgencyPrimePackage";
import AgencyPrimeApproval from "./pages/agency/AgencyPrimeApproval";
import AgencyWallet from "./pages/agency/AgencyWallet";
import UserDashboardV2 from "./pages/UserDashboardV2";
import UserDashboard2Shell from "./pages/v2/UserDashboard2Shell";
import HomeV2 from "./pages/v2/HomeV2";
import LoginV2 from "./pages/v2/Auth/LoginV2";
import RegisterV2V2 from "./pages/v2/Auth/RegisterV2";
import AboutV2 from "./pages/v2/AboutV2";
import PrivacyV2 from "./pages/v2/PrivacyV2";
import TermsV2 from "./pages/v2/TermsV2";
import ContactV2 from "./pages/v2/ContactV2";
import "./css/bms-home.css";
import UserApp from "./pages/UserApp";
import AboutPage from "./pages/AboutPage";
import PrimePage from "./pages/PrimePage";
import BusinessPage from "./pages/BusinessPage";
import DashboardContent from "./pages/DashboardContent";
import EcommerceUser from "./pages/ecommerceUser";
import AdminEcommerceCategories from "./pages/admin/AdminEcommerceCategories";
import CategoryPage from "./pages/CategoryPage";
import AdminUIConfig from "./pages/admin/AdminUIConfig";
import AdminHolidaySupportControls from "./pages/admin/AdminHolidaySupportControls";
import AdminCommissionConfig from "./pages/admin/AdminCommissionConfig";
import AdminSeedDemoData from "./pages/admin/AdminSeedDemoData";
import RankUpgrade from "./pages/RankUpgrade";
import AdminRankUpgrades from "./pages/admin/AdminRankUpgrades";
import UploadToWallet from "./pages/UploadToWallet";
import WalletDashboardV2 from "./pages/consumer/WalletDashboardV2";
import TeamWallet from "./screens/TeamWallet";
import TeamDashboard from "./pages/team/TeamDashboard";
const FranchiseDashboard = lazy(() => import("./components/franchise/FranchiseDashboard"));
const FranchiseMobileHub = lazy(() => import("./components/franchise/FranchiseMobileHub"));
const FranchiseWalletPlaceholder = lazy(() => import("./components/franchise/FranchiseWalletPlaceholder"));
import TransactionHistory from "./components/franchise/pages/TransactionHistory";
import WithdrawalHistory from "./components/franchise/pages/WithdrawalHistory";
import FranchiseMonthlyReport from "./components/franchise/pages/FranchiseMonthlyReport";
import FranchiseEducationPDFs from "./components/franchise/pages/FranchiseEducationPDFs";
import FranchiseAgreementCopy from "./components/franchise/pages/FranchiseAgreementCopy";
import HubbleGiftCards from "./pages/HubbleGiftCards";
import GiftCardSummary from "./pages/GiftCardSummary";
import AdminFranchiseDocuments from "./pages/admin/AdminFranchiseDocuments";
import LayersBlocks from "./pages/team/LayersBlocks";
import AdminTotalAdminCharges from "./pages/admin/AdminTotalAdminCharges";
import AdminOverheadIncome from "./pages/admin/AdminOverheadIncome";

function GrowthRegistrationRoute({ children }) {
  if (isGrowthProduction()) {
    return <Navigate to="/login" replace />;
  }
  return children;
}

function LegacyAuthEntry() {
  const location = useLocation();
  try {
    const params = new URLSearchParams(location.search || "");
    const mode = String(params.get("mode") || "").toLowerCase();
    if (mode === "register") {
      if (isGrowthProduction()) {
        return <Navigate to="/login" replace />;
      }
      const role = String(params.get("role") || "user").toLowerCase();
      const sponsor =
        params.get("sponsor") ||
        params.get("sponsor_id") ||
        params.get("agencyid") ||
        params.get("ref");
      const qs = sponsor ? `?sponsor=${encodeURIComponent(sponsor)}` : "";
      return <Navigate to={`/auth/register/${role}${qs}`} replace />;
    }
  } catch (_) {}
  return <Login />;
}

function DomainRedirects() {
  // Client-side redirect to enforce asiyapp.com:
  // If anyone lands on legacy growth.vin, instantly forward to asiyapp.com
  const location = useLocation();

  try {
    const host = String(window.location.hostname || "").toLowerCase();
    const path = String(location.pathname || "");
    const qs = window.location.search || "";
    const hash = window.location.hash || "";

    if (host.includes("growth.vin")) {
      window.location.replace(`https://asiyapp.com${path}${qs}${hash}`);
      return null;
    }
  } catch (_) {
    // fail open
  }

  return null;
}

function RankUpgradeSsoRedirect() {
  React.useEffect(() => {
    const token =
      (typeof localStorage !== "undefined" && (localStorage.getItem("token_user") || localStorage.getItem("token"))) ||
      (typeof sessionStorage !== "undefined" && (sessionStorage.getItem("token_user") || sessionStorage.getItem("token"))) ||
      "";
    const userRaw =
      (typeof localStorage !== "undefined" && (localStorage.getItem("user_user") || localStorage.getItem("user"))) ||
      (typeof sessionStorage !== "undefined" && (sessionStorage.getItem("user_user") || sessionStorage.getItem("user"))) ||
      "{}";
    let phone = "";
    try {
      const u = JSON.parse(userRaw);
      phone = u.phone || u.username || "";
    } catch (_) {}
    const cleanPhone = String(phone || "").replace(/\D/g, "").slice(-10) || phone;
    const url = `https://trieducation.in/sso?token=${encodeURIComponent(token)}&phone=${encodeURIComponent(cleanPhone)}`;
    window.location.replace(url);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#0B132B", color: "#ffffff", p: 3 }}>
      <Typography variant="body2" sx={{ color: "#94a3b8" }}>
        Navigating to E-edu Agent Academy (trieducation.in)...
      </Typography>
    </Box>
  );
}

function App() {
  const hostname = window.location.hostname;
  const UNDER_MAINTENANCE = false;

  if (UNDER_MAINTENANCE) {
    const path = window.location.pathname;
    if (!path.startsWith("/admin") && !path.startsWith("/admin-panel")) {
      return (
        <div style={{
          textAlign: "center",
          padding: "100px 20px",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: "#333",
          backgroundColor: "#f7f9fa",
          minHeight: "100vh",
          boxSizing: "border-box"
        }}>
          <div style={{ fontWeight: "bold", fontSize: "24px", color: "#0066cc", marginBottom: "40px" }}>asiyapp</div>
          <h1 style={{ fontSize: "40px", marginBottom: "20px", color: "#1a1a1a" }}>Scheduled Maintenance</h1>
          <p style={{ fontSize: "18px", lineHeight: "1.6", color: "#555", maxWidth: "600px", margin: "0 auto 30px auto" }}>
            We are currently performing scheduled system updates to improve performance and reliability. We will be back online shortly. Thank you for your patience!
          </p>
        </div>
      );
    }
  }

  return (
    <BrowserRouter>
      <LoadingOverlay />
      <DomainRedirects />
      <AppLifecycleSync />
      <Routes>
        {/* Public Routes */}


        <Route path="/v4/home" element={<UserApp />} />
       
        <Route path="/" element={<HomeScreen/>} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/prime" element={<PrimePage />} />
        <Route path="/business" element={<BusinessPage />} />
        <Route path="/c/:slug" element={<ConsumerShell><CategoryPage /></ConsumerShell>} />
        <Route path="/auth/select" element={<RoleSelect />} />
        <Route path="/login" element={<LegacyAuthEntry />} />
        <Route path="/register" element={<GrowthRegistrationRoute><EnhancedLogin /></GrowthRegistrationRoute>} />
        <Route path="/enhanced-login" element={<EnhancedLogin />} />
        {/* Role-scoped auth routes */}
        <Route path="/auth/login/:role" element={<Login />} />
        <Route path="/auth/register/:role" element={<GrowthRegistrationRoute><EnhancedLogin /></GrowthRegistrationRoute>} />
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/register" element={<GrowthRegistrationRoute><EnhancedLogin /></GrowthRegistrationRoute>} />
        <Route path="/auth/register-v2" element={<GrowthRegistrationRoute><RegisterV2 /></GrowthRegistrationRoute>} />
        <Route path="/auth/register-v2/:role" element={<GrowthRegistrationRoute><RegisterV2 /></GrowthRegistrationRoute>} />
        {/* V2 public routes */}
        <Route path="/v2/home" element={<HomeV2 />} />
        <Route path="/v2/about" element={<AboutV2 />} />
        <Route path="/v2/privacy" element={<PrivacyV2 />} />
        <Route path="/v2/terms" element={<TermsV2 />} />
        <Route path="/v2/contact" element={<ContactV2 />} />
        <Route path="/v2/login/:role" element={<LoginV2 />} />
        <Route path="/v2/register/:role" element={<GrowthRegistrationRoute><RegisterV2V2 /></GrowthRegistrationRoute>} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/impersonate" element={<ImpersonateLanding />} />
        <Route path="/agency/impersonate" element={<ImpersonateLanding />} />
        <Route path="/employee/impersonate" element={<ImpersonateLanding />} />
        {/* User Routes */}
        <Route
          path="/user/dashboard"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <UserDashboard embedded />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />

        {/* Packages (bifurcated - redirects to SPP Gift Cards Vault; purchases on TriAcademy) */}
        <Route
          path="/user/packages/join-subscription"
          element={<Navigate to="/user/spp-gift-cards" replace />}
        />
        <Route
          path="/user/packages/spp"
          element={<Navigate to="/user/spp-gift-cards" replace />}
        />
        <Route
          path="/user/spp-gift-cards"
          element={
            <ProtectedRoute>
              <ConsumerShell>
                <SPPGiftCards />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/packages/spp-gift-cards"
          element={
            <ProtectedRoute>
              <ConsumerShell>
                <SPPGiftCards />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/packages/digital-education-prime"
          element={
            <ProtectedRoute>
              <ConsumerShell>
                <DigitalEducationPrime />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/educational-videos"
          element={
            <ProtectedRoute>
              <ConsumerShell>
                <EducationalVideoSummary />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/ecommerce"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <EcommerceUser embedded />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/franchise/ecommerce"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <EcommerceUser embedded />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />

        {/* Franchise (Geo agency) Routes */}
        <Route
          path="/franchise/dashboard"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Suspense fallback={<LoadingOverlay />}>
                <FranchiseDashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />

        {/* Agency - Franchise screens (new UI, kept inside the existing /agency area) */}
        <Route
          path="/franchise/hub"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Suspense fallback={<LoadingOverlay />}>
                <FranchiseMobileHub />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/hub"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Suspense fallback={<LoadingOverlay />}>
                <FranchiseMobileHub />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/mobile-hub"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Suspense fallback={<LoadingOverlay />}>
                <FranchiseMobileHub />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/franchise-dashboard"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Suspense fallback={<LoadingOverlay />}>
                <FranchiseDashboard />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/franchise-wallet"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Suspense fallback={<LoadingOverlay />}>
                <FranchiseWalletPlaceholder />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/transactions"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <TransactionHistory />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/withdrawals"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <WithdrawalHistory />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/monthly-report"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <FranchiseMonthlyReport />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/education-pdfs"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <FranchiseEducationPDFs />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/franchise-agreement"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <FranchiseAgreementCopy />
              </AgencyShell>
            </ProtectedRoute>
          }
        />

        {/* Franchise main entry */}
        <Route path="/franchise" element={<Navigate to="/franchise/dashboard" replace />} />

       
        <Route
          path="/user/dashboard2"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <UserDashboardV2 embedded />
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/lucky-draw"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <LuckyDraw embedded />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/redeem-coupon"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <ConsumerCoupon />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/coupon-pocket"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              {isGrowthProduction() ? (
                <Navigate to="/user/team-dashboard" replace />
              ) : (
                <ConsumerShell>
                  <CouponPocket />
                </ConsumerShell>
              )}
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/package-coupon-pocket"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              {isGrowthProduction() ? (
                <Navigate to="/user/team-dashboard" replace />
              ) : (
                <ConsumerShell>
                  <PackageCouponPocket />
                </ConsumerShell>
              )}
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/gift-cards"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <HubbleGiftCards />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/gift-card-summary"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <GiftCardSummary />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/upload-wallet"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <UploadToWallet />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/wallet"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <Wallet />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/withdrawal"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <Wallet />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/wallet-dashboard"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <WalletDashboardV2 />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/team-wallet"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <TeamWallet />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/team-history"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <TeamWalletHistory />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/prime-invoices"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <PrimeInvoices />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/history"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <History />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/kyc"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <ConsumerKYC />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/dashboard/upgrade"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <RankUpgrade />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />

        <Route path="/user/rank-upgrade" element={<RankUpgradeSsoRedirect />} />
        <Route path="/rank-upgrade" element={<RankUpgradeSsoRedirect />} />

        <Route
          path="/admin/rank-upgrades"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminRankUpgrades />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/total-admin-charges"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTotalAdminCharges />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/overhead-income"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminOverheadIncome />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/user/profile"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <Profile />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/my-team"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <MyTeam />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/team/layers-blocks"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <LayersBlocks />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/layers-blocks"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <LayersBlocks />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/genealogy-5"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <Genealogy5 />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/team-dashboard"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <TeamDashboard />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/trikonekt-pdf"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <TeamDocumentDownload kind="PDF" />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/certificate-download"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <TeamDocumentDownload kind="CERTIFICATE" />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route path="/user/genealogy" element={<Navigate to="/user/genealogy-5" replace />} />
        <Route
          path="/user/wealth-galaxy"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <WealthGalaxy />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/app-hub"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <AppHub />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/support"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <Support />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/e-coupon-store"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <ECouponStore />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/package-coupon-pocket"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <PackageCouponPocket />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/promo-packages"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <PackageSummary />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/promo-products"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <PromoProducts />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/cart"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <Cart />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/checkout"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <CheckoutV2 />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/checkout/success"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <CheckoutSuccess />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/payment"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <Payment />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/user/my-orders"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <MyOrdersAll />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        {/* Special TRI app: tri-local-store -> Merchant Shops listing */}
        <Route
          path="/user/tri/tri-local-store"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <MerchantShops />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        {/* Generic TRI apps by slug */}
        <Route
          path="/user/tri/:slug"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <TriAppPage />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/upload"
          element={<Navigate to="/user/lucky-draw" replace />}
        />

        {/* Agency Routes */}
        <Route
          path="/agency/dashboard"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyDashboard />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/lucky-coupons"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyLuckyCoupons />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/my-team"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <MyTeam />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/daily-report"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyDailyReport />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/profile"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <Profile />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/wallet"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Navigate to="/franchise/dashboard?tab=history" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/history"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <Navigate to="/franchise/dashboard?tab=history" replace />
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/refer-earn"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyReferAndEarn />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/coupons"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyCoupons />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/prime-package"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyPrimeApproval />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/prime-approval"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyPrimeApproval />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/support"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <Support />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/e-coupon-store"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <ECouponStore />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/cart"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <Cart />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/checkout"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <CheckoutV2 />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/checkout/success"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <CheckoutSuccess />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/payment"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <Payment />
              </AgencyShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/agency/banners"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <BannerManage />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/purchase-requests"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <PurchaseRequests />
              </AgencyShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/agency/join-prime"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <PromoPackages title="Agent Subscription" />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        {/* Employee Routes */}
        <Route
          path="/employee/dashboard"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <EmployeeDashboard embedded />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/lucky-coupons"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeLuckyCoupons />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/my-team"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <MyTeam />
            </ProtectedRoute>
          }
        />

        <Route
          path="/employee/daily-report"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <EmployeeDailyReport />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/profile"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <Profile />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/wallet"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <Wallet />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/history"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <History />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/support"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <Support />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/e-coupon-store"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <ECouponStore />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/cart"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <Cart />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/checkout"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <CheckoutV2 />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/checkout/success"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <CheckoutSuccess />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/payment"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <Payment />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/user/refer-earn"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <ReferAndEarnPage />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />
        {/* Trikonekt Products & Marketplace Redirects */}
        <Route path="/promo-products" element={<Navigate to="/user/packages/spp" replace />} />
        <Route path="/trikonekt-products" element={<Navigate to="/user/packages/spp" replace />} />
        <Route path="/trikonekt-products/products/:id" element={<Navigate to="/user/packages/spp" replace />} />
        {/* Agency Marketplace visible to consumers */}
        <Route path="/agency-marketplace" element={<ConsumerShell><AgencyMarketplace /></ConsumerShell>} />
        <Route path="/agency-marketplace/banners/:id" element={<ConsumerShell><BannerDetails /></ConsumerShell>} />
        {/* Backward compatibility redirects */}
        <Route path="/marketplace" element={<Navigate to="/user/packages/spp" replace />} />
        <Route path="/marketplace/products/:id" element={<Navigate to="/user/packages/spp" replace />} />
        <Route path="/marketplace/banners/:id" element={<Navigate to="/agency/marketplace/banners/:id" replace />} />
        <Route
          path="/marketplace/my-orders"
          element={
            <ProtectedRoute allowedRoles={["user"]}>
              <ConsumerShell>
                <MyOrders />
              </ConsumerShell>
            </ProtectedRoute>
          }
        />

        {/* Agency - Products and Purchase Requests */}
        <Route
          path="/agency/trikonekt-products"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <TrikonektProducts />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/promo-products"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <PromoProducts />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/trikonekt-products/products/:id"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <ProductDetails />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        {/* Agency Marketplace (Agency banners-only) */}
        <Route
          path="/agency/marketplace"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <AgencyMarketplace />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/agency/marketplace/banners/:id"
          element={
            <ProtectedRoute allowedRoles={["agency"]}>
              <AgencyShell>
                <BannerDetails />
              </AgencyShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/e-coupon-store"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <ECouponStore />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/trikonekt-products"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <TrikonektProducts />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/promo-products"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <PromoProducts />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/employee/trikonekt-products/products/:id"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <ProductDetails />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/employee/join-prime"
          element={
            <ProtectedRoute allowedRoles={["employee"]}>
              <EmployeeShell>
                <PromoPackages title="Agent Subscription" />
              </EmployeeShell>
            </ProtectedRoute>
          }
        />
        {/* Admin Routes */}
        <Route
          path="/admin/franchise"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <Navigate to="/admin/franchise/dashboard" replace />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseDashboard />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/users"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseUsers />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/category/:category"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseCategoryDashboard />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/wallets"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminWallets />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/withdrawals"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminWithdrawals />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/inactive-work-window"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseWalletControls />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/rewards"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseWalletControls />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/work-approvals"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseWalletControls />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/monthly-entry-report"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseWalletControls />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/education-pdf-upload"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseDocuments />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/agreement-template"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseDocuments />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/kyc"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminKYC audience="franchise" />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/commissions/distribute"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminCommissionDistribute />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/validator-commission"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminValidatorCommission audience="franchise" />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/validator-commission"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminValidatorCommission audience="consumer" />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/commissions"
          element={<Navigate to="/admin/franchise/commissions/distribute" replace />}
        />
        <Route
          path="/admin/franchise/pools/monitor"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminAutopool />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminDashboard />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/user-tree"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminUserTree />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminAnalytics />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminUsers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/merchants"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminMerchants />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/kyc"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminKYC />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/withdrawals"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWithdrawals />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />

        {/* Franchise: Admin-managed achievers + wishing banners */}
        <Route
          path="/admin/franchise/achievers"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseAchievers />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/wishing-banners"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseWishingBanners />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/support"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseSupport />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/customer-care"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchiseSupport />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/franchise/:module"
          element={
            <AdminProtectedRoute>
              <AdminFranchiseShell>
                <AdminFranchisePlaceholder />
              </AdminFranchiseShell>
            </AdminProtectedRoute>
          }
        />

        {/* Team/Consumer: Admin-managed wishing banners + top achievers */}
        <Route
          path="/admin/team-consumer/block-users"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTeamConsumerBlockUsers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/team-consumer/wishing-banners"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTeamConsumerWishingBanners />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/team-consumer/top-achievers"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTeamConsumerTopAchievers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/team-consumer/educational-videos"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTeamConsumerEducationalVideos />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/team-consumer/pdf-uploads"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTeamConsumerPdfUploads />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/team-consumer/certificate-uploads"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTeamConsumerCertificateUploads />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/matrix"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <Navigate to="/admin/matrix/five" replace />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/matrix/five"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminMatrixFive />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/matrix/three"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminMatrixThree />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/autopool"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminAutopool />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/matrix-reconcile"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminMatrixReconcile />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/e-coupons"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminECoupons />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ssv-vouchers"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminSSVVouchers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/agent-training-vouchers"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminAgentTrainingVouchers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/cycle-management"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminCycleManagement />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/term-management"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminCycleManagement />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/commission-config"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminCommissionConfig />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/payments"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminPayments />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/agency-prime-requests"
          element={
            <AdminProtectedRoute>
              <Navigate to="/admin/franchise/validator-commission" replace />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/promo-purchases"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminPromoPurchases />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/wallet-upload-approvals"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletUploadApprovals />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallet-command-center"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletCommandCenter />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallet-ledger"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletLedger />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/team-wallet-dashboard"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTeamWalletDashboard />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallet-monitoring"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletMonitoring />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/package-management"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminPackageManagement />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reward-distribution"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminRewardDistribution />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallet-settlements"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletSettlements />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallets"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWallets />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallets/:userId"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletDetail />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallet-vouchers"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletVouchers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/wallet-reconcile"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletReconcile />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/user-audit"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminUserAudit />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics/debugger"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWalletDebugger />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ledger-statement"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminLedgerStatement />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/analytics/sales"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminDailySalesReport />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reports/daily-report"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminDailySalesReport />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />

        {/* Package-specific Admin screens (single screen per package) */}
        <Route
          path="/admin/packages/join-subscription"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminJoinSubscriptionPurchases />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/packages/spp"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminSPPPurchases />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/packages/spp-seasons"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminSPPSeasons />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/packages/tri-tour"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTriTourPurchases />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ledger/join-prime"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminJoinPrimeLedger />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ledger/spp"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminSPPLedger />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ledger/tri-tour"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTriTourLedger />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ledger/digital-education"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminDigitalEducationLedger />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/promo-package-products"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminPromoPackageProducts />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/products"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminProducts />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/banners"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminBanners />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/orders"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminOrders />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/packages"
          element={
            <AdminProtectedRoute>
              <Navigate to="/admin/franchise/validator-commission" replace />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/uploads"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminUploads />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/business"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminBusiness />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminReports />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reports/internal-wallet"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminInternalWalletDailyReport />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/reports/users-today"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminUsersTodayReport />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/support"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminSupport />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/notifications"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminNotifications />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ecommerce-categories"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminEcommerceCategories />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/ui-config"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminUIConfig />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/holiday-support-controls"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminHolidaySupportControls />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/seed-demo"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminSeedDemoData />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard-cards"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminDashboardCards />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/home-cards"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminHomeCards />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/lucky-draw"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminLuckyDraw />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/commissions/matrix"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminMatrixCommission />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/commissions/levels"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminLevelCommission />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/commissions/distribute"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminCommissionDistribute />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/commissions/history"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminCommissionHistory />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/rewards/points"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminRewardPoints />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/tri/:slug"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminTriAppManage />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/access-manager"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminAccessManager />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        {/* RBAC Administration routes */}
        <Route
          path="/admin/sub-admins"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminAdminUsers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/packages/digital-education-prime"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminDigitalEducationPrimeApprovals />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <RolesPage />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/permissions"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <PermissionsPage />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/user-permissions"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <UserPermissionMap />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        {/* Legacy RBAC URLs kept as aliases */}
        <Route
          path="/admin_user"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminAdminUsers />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/role"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <RolesPage />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/permission"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <PermissionsPage />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/user_permission"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <UserPermissionMap />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />

        <Route
          path="/admin/dashboard/models"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <ModelsIndex />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard/models/:app/:model"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <ModelList />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard/examples/users"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <UsersPage />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard/examples/products"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <ProductPage />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/merchant-categories"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminMerchantCategories />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/merchant-subcategories"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminMerchantSubcategories />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/workflows/:slug"
          element={
            <AdminProtectedRoute>
              <AdminShell>
                <AdminWorkflowScreen />
              </AdminShell>
            </AdminProtectedRoute>
          }
        />
        {/* Business (Merchant) Routes */}
        <Route
          path="/business/dashboard"
          element={
            <ProtectedRoute allowedRoles={["business"]}>
              <BusinessShell>
                <BusinessDashboard />
              </BusinessShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/profile"
          element={
            <ProtectedRoute allowedRoles={["business"]}>
              <BusinessShell>
                <BusinessProfile />
              </BusinessShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/shops"
          element={
            <ProtectedRoute allowedRoles={["business"]}>
              <BusinessShell>
                <BusinessShops />
              </BusinessShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/shops/:shopId/products"
          element={
            <ProtectedRoute allowedRoles={["business"]}>
              <BusinessShell>
                <BusinessShopProducts />
              </BusinessShell>
            </ProtectedRoute>
          }
        />
        <Route
          path="/business/support"
          element={
            <ProtectedRoute allowedRoles={["business"]}>
              <BusinessShell>
                <Support />
              </BusinessShell>
            </ProtectedRoute>
          }
        />

        <Route
          path="/business/join-prime"
          element={
            <ProtectedRoute allowedRoles={["business"]}>
              <BusinessShell>
                <PromoPackages title="Agent Subscription" />
              </BusinessShell>
            </ProtectedRoute>
          }
        />
        {/* Merchant Marketplace (Public) */}
        <Route path="/merchant-marketplace" element={<ConsumerShell><MerchantShops /></ConsumerShell>} />
        <Route path="/merchant-marketplace/shops/:id" element={<ConsumerShell><MerchantShopDetail /></ConsumerShell>} />

        {/* Catch-all route for unknown paths */}
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
